import React, { memo, useRef, useState } from 'react';
import ImageComponent from '@/components/UiElements/ImageComponent';
import Checkbox from '@/components/UiElements/Checkbox';
import { FlattedCategory } from '@/types/category';

interface ImageButtonProps {
    graphic: FlattedCategory;
    onClick?: (category: FlattedCategory) => void;
    isSelected?: boolean;
    onToggleSelect?: (graphic: FlattedCategory, shiftKey: boolean) => void;
}

const ImageButton: React.FC<ImageButtonProps> = (
    {
        graphic,
        onClick,
        isSelected,
        onToggleSelect,
    }
) => {


    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isFadingOut, setIsFadingOut] = useState(false);
    const shiftSelectionRef = useRef(false);

    const classNames = {
        wrapper: 'relative block h-[150px] group',
        frame: '!bg-transparent overflow-visible !p-[0px] !border-none !m-[6px] relative !h-[100%] w-[100%] ',
        content: `!cursor-zoom-in relative flex flex-col w-full h-full border ${isSelected ? 'border-primary-500' : 'border-gray-300'} rounded-md justify-between items-center bg-white group-hover:border-gray-500 transition-all p-[5px] z-[2]`,
        fullScreenImage: `fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black z-50 bg-white p-xxxl cursor-zoom-out transition-opacity duration-200 ${isFadingOut ? 'opacity-0' : 'opacity-100'}`,
        fullScreenImageContent: `max-w-full max-h-full  ${isFadingOut ? "top-[25px]" : "top-[0px]"} transition-all duration-200`,
    };

    const handleClickDeleteButton = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onClick?.(graphic);
    };

    const handleImageClick = () => {
        if (isFullScreen) {
            setIsFadingOut(true);
            setTimeout(() => {
                setIsFullScreen(false);
                setIsFadingOut(false);
            }, 300); // Match the duration of the fade-out animation
        } else {
            setIsFullScreen(true);
        }
    };

    const handleToggleSelection = () => {
        onToggleSelect?.(graphic, shiftSelectionRef.current);
        shiftSelectionRef.current = false;
    };

    return (
        <div
            className={classNames.wrapper}
            data-category-graphic-item
            data-category-graphic-id={graphic.id}
        >
            <div className={classNames.frame}>
                <div className={classNames.content} onClick={handleImageClick}>
                    {isSelected && (
                        <div className="pointer-events-none absolute inset-0 z-[3] rounded-md bg-primary-500/20 mix-blend-multiply"/>
                    )}
                    <div
                        className="absolute top-xs left-xs z-[1]"
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            shiftSelectionRef.current = e.shiftKey;
                        }}
                        onKeyDown={(e) => {
                            shiftSelectionRef.current = e.shiftKey;
                        }}
                    >
                        <Checkbox
                            label={`Select ${graphic.name}`}
                            defaultChecked={Boolean(isSelected)}
                            onChange={handleToggleSelection}
                            className={() => '!gap-0 [&>span]:sr-only'}
                        />
                    </div>
                    <button
                        type="button"
                        aria-label={`Delete ${graphic.name}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleClickDeleteButton}
                        className={
                            "bg-transparent hover:bg-red-500 hover:text-white active:bg-red-600 active:text-white " +
                            "text-red-500 text-sm font-icon-regular absolute top-xs right-xs w-[35px] h-[35px] " +
                            "opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus:opacity-100 focus:pointer-events-auto " +
                            "transition-all rounded-md flex items-center justify-center z-[1] cursor-pointer"
                        }
                    >
                        
                    </button>

                    <div className="flex-1 flex justify-center items-center w-full relative">
                        <ImageComponent
                            src={graphic.img}
                            className="!max-h-[80px] "
                        />
                    </div>
                </div>
            </div>
            <style>
                {`
                    .zoomImage svg {
                        margin: auto;
                        border: solid 1px #ccc;
                        width: auto;
                        border-radius: 8px;
                        max-width: 100%;
                        max-height: 100%;
                    }
                `}
            </style>
            {isFullScreen && (
                <div className={classNames.fullScreenImage} onClick={handleImageClick}>
                    <div className={"zoomImage"}>
                        <ImageComponent
                            src={graphic.img}
                            className={classNames.fullScreenImageContent}
                            style={{ maxHeight: '90vh', maxWidth: '90vw', width: '70vw', height: '70vh' }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default memo(ImageButton, (prevProps, nextProps) =>
    prevProps.graphic.id === nextProps.graphic.id &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.onToggleSelect === nextProps.onToggleSelect
);
